import { isBefore, isValid, parse, differenceInYears } from 'date-fns/fp'
import { parsePhoneNumberFromString } from 'libphonenumber-js/max'
import { parsePhoneNumberFromString as parseMobilePhoneNumberFromString } from 'libphonenumber-js/mobile'
import * as Yup from 'yup'

import { LONG_BDAY_FORMAT, BirthdayValidationMessageObject, Disease } from '../../../ana-toolbox'
import { pipe } from '../helpers/pipe'
import { isInBetween, isLessOrEq } from '../helpers/predicates'

import { emailValidationHelpers } from './emailValidation'
import { LATIN_CHARACTERS_ERROR, areLatinTexts, isLatinText } from './latinValidation'

const defaultBirthdayErrorMessages: BirthdayValidationMessageObject = {
  invalidDate: 'Please insert a valid date. For example 31.12.1990.',
  inPast: 'Please insert a date in the past.',
  empty: 'This field is required.',
  adult: 'Please enter a valid date. The insurance holder’s age must be above 18 years.',
  maxAge: 'Please choose an age between 1 and 120 years',
}

const MAXIMUM_AGE = 120
const TEEN_AGE = 18

/* This function validates a string which represents
 * a date in the DD.MM.YY, DD.MM.YYYY, or YYYY-MM-DD format
 */
export function dateValidation(
  this: Yup.StringSchema<string | undefined>,
  message: Partial<BirthdayValidationMessageObject> | undefined
): Yup.StringSchema<string | undefined> {
  return this.test('birthday', '', function (this: Yup.TestContext, value: string | null | undefined) {
    const { path, createError } = this

    if (!value)
      return createError({
        path,
        message: message?.empty || defaultBirthdayErrorMessages.empty,
      })

    const trimmedValue = value.trim()

    // Check if the value is in YYYY-MM-DD format
    const isISOFormat = /^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)

    // For traditional format (DD.MM.YY or DD.MM.YYYY), check length
    if (!isISOFormat && trimmedValue.length !== LONG_BDAY_FORMAT.length)
      return createError({
        path,
        message: message?.invalidDate || defaultBirthdayErrorMessages.invalidDate,
      })

    // Parse the date according to its format
    const date = isISOFormat
      ? new Date(trimmedValue) // Parse YYYY-MM-DD format
      : parse(new Date())(LONG_BDAY_FORMAT)(value) // Parse traditional formats (DD.MM.YY or DD.MM.YYYY)

    const now = new Date()
    const isDateValid = isValid(date)

    const isPast = isBefore(now)(date)

    if (!isDateValid)
      return createError({
        path,
        message: message?.invalidDate || defaultBirthdayErrorMessages.invalidDate,
      })

    if (!isPast)
      return createError({
        path,
        message: message?.inPast || defaultBirthdayErrorMessages.inPast,
      })

    return isDateValid
  })
}

export function adultValid(
  this: Yup.StringSchema<string | undefined>,
  message?: string
): Yup.StringSchema<string | undefined> {
  return this.test('adult', '', function (this: Yup.TestContext, value: string | null | undefined) {
    const { path, createError } = this

    if (!value)
      return createError({
        path,
        message: defaultBirthdayErrorMessages.empty,
      })
    const trimmedValue = value.trim()

    // Check if the value is in YYYY-MM-DD format
    const isISOFormat = /^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)
    // Parse the date according to its format
    const date = isISOFormat
      ? new Date(trimmedValue) // Parse YYYY-MM-DD format
      : parse(new Date())(LONG_BDAY_FORMAT)(value) // Parse traditional formats (DD.MM.YY or DD.MM.YYYY)

    const now = new Date()

    const isAdult = pipe(differenceInYears(date), isInBetween(TEEN_AGE, MAXIMUM_AGE))(now)

    if (!isAdult)
      return createError({
        path,
        message: message || defaultBirthdayErrorMessages.adult,
      })

    return isAdult
  })
}

export function maxAgeValid(
  this: Yup.StringSchema<string | undefined>,
  message?: string
): Yup.StringSchema<string | undefined> {
  return this.test('maxAge', '', function (this: Yup.TestContext, value: string | null | undefined) {
    const { path, createError } = this

    if (!value)
      return createError({
        path,
        message: defaultBirthdayErrorMessages.empty,
      })

    const date = parse(new Date())(LONG_BDAY_FORMAT)(value)
    const now = new Date()

    const isLessThanMaximumAge = pipe(differenceInYears(date), isLessOrEq(MAXIMUM_AGE))(now)

    if (!isLessThanMaximumAge)
      return createError({
        path,
        message: message || defaultBirthdayErrorMessages.maxAge,
      })

    return isLessThanMaximumAge
  })
}

export function addressValidation(
  this: Yup.StringSchema<string | undefined>,
  message?: string
): Yup.StringSchema<string | undefined> {
  return this.test('address', '', function (this: Yup.TestContext, value: string | null | undefined) {
    const { path, createError } = this

    const haveCorrectSize = value ? value.length > 3 : false
    const haveNumber = value ? / [0-9]{1,}/.test(value) : false

    const valid = haveCorrectSize && haveNumber

    if (!valid)
      return createError({
        path,
        message: message || 'Please enter a valid address (at least 3 characters, 1 space and a number)',
      })

    return true
  })
}

export function emailValidation(
  this: Yup.StringSchema<string | undefined>,
  message?: string
): Yup.StringSchema<string | undefined> {
  return this.test('customEmail', '', function (this: Yup.TestContext, value: string | null | undefined) {
    const { path, createError } = this

    if (!value) return true

    const valid = emailValidationHelpers.isValid(value || '')

    if (valid) {
      return true
    }

    return createError({ path, message })
  })
}

/**
 * Allow any type of phone number
 */
export function phoneValidation(
  this: Yup.StringSchema<string | undefined>,
  message?: string
): Yup.StringSchema<string | undefined> {
  return this.test('phoneNumber', '', function (this: Yup.TestContext, value: string | null | undefined) {
    const { path, createError } = this

    if (!value || typeof value !== 'string') return true

    try {
      const valid = parsePhoneNumberFromString(value)?.isValid()

      if (valid) return valid
    } catch (e) {
      console.error(e)
    }

    return createError({ path, message })
  })
}

/**
 * Allow only mobile phone numbers
 */
export function mobilePhoneValidation(
  this: Yup.StringSchema<string | undefined>,
  message?: string
): Yup.StringSchema<string | undefined> {
  return this.test('mobilePhoneNumber', '', function (this: Yup.TestContext, value: string | null | undefined) {
    const { path, createError } = this

    if (!value || typeof value !== 'string') return true

    try {
      const valid = parseMobilePhoneNumberFromString(value)?.isValid()

      if (valid) return valid
    } catch (e) {
      console.error(e)
    }

    return createError({ path, message })
  })
}

/**
 * Allow only characters of the Latin script (see `latinValidation.ts`)
 */
export function latinValidation(
  this: Yup.StringSchema<string | undefined>,
  message: string = LATIN_CHARACTERS_ERROR
): Yup.StringSchema<string | undefined> {
  return this.test('latin', message, (value: string | null | undefined) => !value || isLatinText(value))
}

/* The free-text lists are validated as a whole, so a non-Latin entry produces one error on the
 * list itself (e.g. `errors.medications`) instead of one per entry, which is the shape the forms
 * and the summary know how to display.
 */
export const latinTextListSchema = Yup.array()
  .of(Yup.string())
  .test('latinList', LATIN_CHARACTERS_ERROR, (values?: Array<string | undefined> | null) => areLatinTexts(values || []))

/* Covers the names of the custom diseases, surgeries and allergies, and the medications taken for them */
export const latinDiseasesSchema = Yup.array<Disease>().test(
  'latinDiseases',
  LATIN_CHARACTERS_ERROR,
  (diseases?: Disease[] | null) =>
    areLatinTexts((diseases || []).flatMap(({ name, medications = [] }) => [name, ...medications]))
)
